import { cn } from '@mdxui/primitives/lib/utils'
import { Badge, Button } from '@mdxui/primitives'
import { formatDate } from '../lib/utils'
import type { ComponentType, ReactNode } from 'react'
import type { BlogPost } from './blog-card'
import { ChevronRight } from 'lucide-react'

export interface ReadMoreLinkProps {
  href: string
  children: ReactNode
  className?: string
}

export interface ReadMoreSectionProps {
  /** Related posts to display */
  posts: BlogPost[]
  /** Current post slug (to exclude from list) */
  currentSlug: string
  /** Current post tags (for relevance scoring) */
  currentTags?: string[]
  /** Maximum posts to show (default: 3) */
  maxPosts?: number
  /** Custom link component */
  LinkComponent?: ComponentType<ReadMoreLinkProps>
  /** Base path for post links (default: '') — e.g. '/blog' produces '/blog/{slug}' */
  basePath?: string
  /** Additional className */
  className?: string
}

/**
 * Default link component
 */
function DefaultLink({ href, children, className }: ReadMoreLinkProps) {
  return (
    <a href={href} className={className}>
      {children}
    </a>
  )
}

/**
 * Score and sort posts by relevance (tag overlap) then recency
 */
function getRelatedPosts(
  posts: BlogPost[],
  currentSlug: string,
  currentTags: string[],
  maxPosts: number
): BlogPost[] {
  return posts
    .filter((post) => post.slug !== currentSlug)
    .map((post) => {
      const tagOverlap = currentTags.filter((tag) => post.tags?.includes(tag)).length
      return { post, score: tagOverlap }
    })
    .sort((a, b) => {
      if (a.score !== b.score) return b.score - a.score
      const dateA = a.post.date ? new Date(a.post.date).getTime() : 0
      const dateB = b.post.date ? new Date(b.post.date).getTime() : 0
      return dateB - dateA
    })
    .slice(0, maxPosts)
    .map(({ post }) => post)
}

export function ReadMoreSection({
  posts,
  currentSlug,
  currentTags = [],
  maxPosts = 3,
  LinkComponent = DefaultLink,
  basePath = '',
  className,
}: ReadMoreSectionProps) {
  const relatedPosts = getRelatedPosts(posts, currentSlug, currentTags, maxPosts)

  if (relatedPosts.length === 0) return null

  return (
    <section className={cn('border-t border-border', className)}>
      <div className="max-w-7xl mx-auto p-6 lg:p-10 pb-16 sm:pb-24">
        <h2 className="text-xl font-medium mb-8">Read more</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
          {relatedPosts.map((post) => (
            <LinkComponent key={post.slug} href={`${basePath}/${post.slug}`} className='group block'>
              <article className='flex flex-col h-full p-4 bg-background border border-border rounded-[var(--radius-lg,var(--radius))] transition-colors group-hover:border-foreground/40'>
                {/* Date */}
                {post.date && <time className='block text-xs font-mono font-medium text-muted-foreground mb-2'>{formatDate(post.date)}</time>}

                {/* Title + Description */}
                <div className='my-3 min-h-[6.5rem]'>
                  <h3 className='text-lg font-semibold text-card-foreground'>{post.title}</h3>
                  {post.description && <p className='text-card-foreground/80 text-sm line-clamp-3 mt-1'>{post.description}</p>}
                </div>

                {/* Footer: Tags + Read button */}
                <div className='flex justify-between items-center mt-auto pt-4'>
                  {/* Tags */}
                  {post.tags && post.tags.length > 0 ? (
                    <div className='flex flex-wrap gap-2'>
                      {post.tags.slice(0, 2).map((tag) => (
                        <Badge key={tag} variant='secondary'>
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <div />
                  )}

                  {/* Read button */}
                  <Button variant='link' className='text-muted-foreground group-hover:text-foreground hover:no-underline transition-colors'>
                    Read <ChevronRight className='w-4 h-4' />
                  </Button>
                </div>
              </article>
            </LinkComponent>
          ))}
        </div>
      </div>
    </section>
  )
}
